import classNames from "classnames";
import { useState } from "react";
import useTranslation from "../../hooks/useTranslation";
import { Activity } from "../../models/activity";
import { getTooltipTitle } from "../../utils/ActionPlan.utils";
import { UpgradeSubscriptionForm } from "../UpgradeSubscriptionForm/UpgradeSubscriptionForm";
import { Button } from "../ui/Button/Button";
import { Chip } from "../ui/Chip/Chip";
import { Modal } from "../ui/Modal/Modal";
import { Snackbar } from "../ui/Snackbar/Snackbar";
import { Tooltip } from "../ui/Tooltip/Tooltip";
import { Typography } from "../ui/Typography/Typography";
import { IconTypes } from "../ui/icons";
import { Icon } from "../ui/icons/Icon";
import "./ActionPlanTimeline.scss";

interface ActionPlanSwimlaneProps {
  title: string;
  icon: IconTypes;
  activities: Activity[];
  startDate: Date;
  endDate: Date;
  now: Date;
  preview?: boolean;
  onActivityClick?: (activity: Activity) => void;
}

export const ActionPlanSwimlane = ({
  title,
  icon,
  activities,
  startDate,
  endDate,
  now,
  preview = false,
  onActivityClick,
}: ActionPlanSwimlaneProps) => {
  const { t, i18n } = useTranslation("dashboardPage");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [snackBarOpen, setSnackBarOpen] = useState(false);

  const handleSuccessfulQuoteRequest = () => {
    setSnackBarOpen(true);
    setIsModalOpen(false);
  };
  const classes = classNames("ActionPlanSwimlane", {
    "ActionPlanSwimlane--preview": preview,
  });
  return (
    <div className={classes}>
      <div className="ActionPlanSwimlane__header">
        <Icon
          className="ActionPlanSwimlane__header__icon"
          type={icon}
          variant="outlined"
          color="secondary"
        />
        <Typography
          desktop="body2"
          color="secondary"
          className="ActionPlanSwimlane__header__title"
        >
          {title}
        </Typography>
      </div>
      <div className="ActionPlanSwimlane__track">
        {preview && (
          <div className="ActionPlanSwimlane__track__previewOverlay">
            <div>
              <Button
                color="info"
                variant="contained"
                onClick={() => setIsModalOpen(true)}
                small
                data-testid="upgrade-subscription-btn"
              >
                <Icon type={IconTypes.Lock} />
                {t("subscriptions.upgrade")}
              </Button>
            </div>
          </div>
        )}
        {activities.map((activity, index) => {
          const layout = activity.getLayout(startDate, endDate);
          const tooltipTitle = getTooltipTitle(
            activity.label,
            t,
            activity.startDate,
            activity.endDate,
            i18n?.language
          );
          const isEditable = activity.isEditable(now);
          const markerClasses = classNames(
            "ActionPlanSwimlane__track__marker",
            {
              "ActionPlanSwimlane__track__marker--bar":
                activity.endDate && activity.events.length > 1,
              "ActionPlanSwimlane__track__marker--disabled": !isEditable,
            }
          );
          return (
            <Tooltip key={`${activity.label}-${index}`} title={tooltipTitle}>
              <div
                className={markerClasses}
                style={{
                  left: layout.left,
                  width: layout.width,
                  backgroundColor: activity.color,
                }}
                onClick={() => isEditable && onActivityClick?.(activity)}
              >
                {activity.events.map(
                  (event, evIndex) =>
                    event.icon && (
                      <div
                        key={evIndex}
                        className="ActionPlanSwimlane__track__marker__icon"
                      >
                        <Icon type={event.icon} color={event.iconColor} />
                      </div>
                    )
                )}
                <Chip
                  className="ActionPlanSwimlane__track__marker__chip"
                  label={activity.label}
                  variant="bubble"
                />
              </div>
            </Tooltip>
          );
        })}
      </div>
      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)} light>
        <UpgradeSubscriptionForm
          onSuccessfulRequestQuote={() => handleSuccessfulQuoteRequest()}
        />
      </Modal>
      <Snackbar
        open={snackBarOpen}
        onClose={() => setSnackBarOpen(false)}
        message={t("subscriptions.quoteRequestSent")}
        autoHideDuration={3000}
      />
    </div>
  );
};
